import { useState, useEffect, useRef } from 'react';
import { Formik, Field } from 'formik';
import axios from 'axios';
import Select from 'react-select';
import * as Yup from 'yup';
import { IoSearchCircleOutline } from 'react-icons/io5';
import { fontSize, fontWeight } from '@mui/system';
import { alphabeticOrder } from '../utils/utils';
import {
  Values, ValuesCategorie, FilterProps, ValuesFacilities
} from '../components/Interfaces';
import Loader from './Loader';
import { find, set } from 'lodash';
import { useNavigate } from 'react-router-dom';
import WarningPopup from './WarningPopup';
import { ModalPreparations } from './ModalPreparations';

const baseURL = process.env.REACT_APP_BASEURL;
const authorization = process.env.REACT_APP_AUTHORIZATION;

async function delay(ms: number) {
  console.log('Aqui antes del await');
  await new Promise((resolve) => setTimeout(resolve, ms));
  console.log('Aqui despues del await');
}

const Filter = ({
  id,
  uid,
  doctorFilter,
  clientData,
  dataLabels,
  setDataHour,
  isCharge,
  dataHour,
}: FilterProps) => {
  const [loadingFacilities, setLoadingFacilities] = useState<boolean>(false);
  const [showFacility, setShowFacility] = useState<boolean>(false);
  const [loadingCategories, setLoadingCategories] = useState<boolean>(false);
  const [showCategories, setShowCategories] = useState<boolean>(false);
  const [specialties, setEspecialties] = useState<Values[]>([{ value: null, label: '' }]);
  const [categories, setCategories] = useState<[ValuesCategorie]>([{ value: null, label: 'Selecciona una prestacion' }]);
  const [doctors, setDoctors] = useState<[Values]>([{ value: null, label: 'Selecciona un doctor' }]);
  const [sucursales, setSucursales] = useState<ValuesFacilities[]>([{} as ValuesFacilities]);
  const [sucursalesDispobibles, setSucursalesDisponibles] = useState<ValuesFacilities[]>([{} as ValuesFacilities]);
  const [showDoctors, setShowDoctors] = useState<boolean>(false);
  const [loadingDoctors, setLoadingDoctors] = useState<boolean>(false);
  const [showWarningModal, setShowWarningModal] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<any>(null);
  const preSelectedSpecialty = id.includes('-') ? Number(id.split('-')[1]) - 1 : null;
  const groupId = id.split('-')[0];
  const navigate = useNavigate();
  const formRef = useRef<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (dataHour.specialty) {
        findCategory(dataHour.specialty);
      }
      if (dataHour.facility) {
        setShowFacility(true);
      }
      if (dataHour.categorie) {
        setShowCategories(true);
        findFacilitiesByCategorie(dataHour.categorie, clientData.edad);
      } else {
        await findFacilities();
      }
      findSpeciality(groupId.toString());
    };

    fetchData();
  }, []);


  const findFacilities = async () => {
    try {
      const response = await axios.post(`${baseURL}/alma/external/pp/v1/facility`, '', {
        headers: {
          Authorization: authorization,
        },
      });
      const sucursalesList: ValuesFacilities[] = [];
      const { data } = response;
      if (Array.isArray(data)) {
        data.forEach((item: any) => sucursalesList.push({
          value: item.id,
          label: item.name,
          street: item.street,
          state: item.state,
        }));
      }
      sucursalesList.sort(alphabeticOrder);
      setSucursalesDisponibles(sucursalesList);
      return sucursalesList;
    } catch (err) {
      setSucursalesDisponibles([]);
    }
    return [];

  };

  const findSpeciality = (idF: string) => {
    axios.post(`${baseURL}/alma/external/v1/specialtyWeb`, {
      group: groupId,
    }, {
      headers: {
        Authorization: authorization,
        'Content-Type': 'multipart/form-data',
      },
    }).then((response: any) => {
      const dataFilter: Array<unknown> = response.data;
      const data: Values[] = [];
      if (Array.isArray(dataFilter)) {
        dataFilter.forEach((item: any) => data.push({
          value: item.option_id,
          label: item.title,
        }));
      }
      setEspecialties(data);
      if (preSelectedSpecialty !== null && data[preSelectedSpecialty]) {
        const selectedSpecialty = data[preSelectedSpecialty];
        findCategory(selectedSpecialty.value as string);
        dataLabels.specialty = selectedSpecialty.label;
        if (formRef.current) {
          formRef.current.setFieldValue('especialidad', selectedSpecialty.value);
        }
      }
    }).catch(() => {});
  };

  async function findCategory(specialty: any) {
    setLoadingCategories(true);
    await axios.post(`${baseURL}/alma/external/pp/v1/categories`,
      {
        specialty,
      },
      {
        headers: {
          Authorization: authorization,
          'Content-Type': 'multipart/form-data',
        },
      }).then((response: any) => {
        const dataFilter: Array<unknown> = response.data;
        const data = Array.isArray(dataFilter)
          ? dataFilter.filter((item: any) => item.visible_agenda_web === 1) : [];
        const categorias: any = [];
        data.forEach((item: any) => {
          const name = item.pc_catname;
          categorias.push({
            value: {
              id: item.pc_catid,
              preparacion: item.pc_preparacion,
              valor_fn1: item.valor_FN1,
              valor_particular: item.valor_particular,
              pc_externo: item.pc_externo,
            },
            label: name,
          });
        });
        categorias.sort(alphabeticOrder);
        setCategories(categorias);
        setLoadingCategories(false);
        setShowCategories(true);
        categorias.sort(alphabeticOrder);
        setCategories(categorias);
      }).catch(() => {});
  }

  const findFacilitiesByCategorie = async (categorie: string, age: string) => {
    const facilities = await findFacilities();
    setLoadingFacilities(true);
    await axios.post(`${baseURL}/alma/external/v1/nextHourCategories`, {
      categorie,
      age,
    }, {
      headers: {
        Authorization: authorization,
        'Content-Type': 'multipart/form-data',
      },
    }).then((response: any) => {
      const { data } = response;
      const sucursalesId = Array.isArray(data)
        ? data.map((sucursal: any) => +sucursal.facility)
        : [];
      const sucursalesFiltradas = facilities.filter(
        (sucursal: any) => sucursalesId.includes(sucursal.value),
      );
      sucursalesFiltradas.sort(alphabeticOrder);
      setSucursales(sucursalesFiltradas);
      setLoadingFacilities(false);
      setShowFacility(true);
    }).catch(() => {});
  };

  const findDoctors = (facility: any, category: any) => {

    setLoadingDoctors(true);
    axios.post(`${baseURL}/alma/external/pp/v1/facilityCategorie`,
      {
        facility,
        categorie: category,
      },
      {
        headers: {
          Authorization: authorization,
          'Content-Type': 'multipart/form-data',
        },
      }).then((response: any) => {
        const { data } = response;
        const doctorsList: any = [];
        data.forEach((item: any) => {
          doctorsList.push({ value: item.id, label: `${item.nombre} ${item.apellido}` });
        });
        doctorsList.sort(alphabeticOrder);
        setDoctors(doctorsList);
        setLoadingDoctors(false);
        setShowDoctors(true);
        setLoadingFacilities(false);
      }).catch(() => {});
  };

  const colourStyles = {
    control: (base: any, state: any) => ({
      ...base,
      backgroundColor: 'white',
      border: state.isFocused ? 0 : 0,
      // This line disable the blue border
      boxShadow: state.isFocused ? 0 : 0,
      '&:hover': {
        border: state.isFocused ? 0 : 0,
      },
      fontWeight: 'lighter',
      fontSize: '14px',
    }),
    menu: (base: any, state: any) => ({
      ...base,
      backgroundColor: 'white',
      border: state.isFocused ? 0 : 0,
      // This line disable the blue border
      boxShadow: state.isFocused ? 0 : 0,
      '&:hover': {
        border: state.isFocused ? 0 : 0,
      },
      fontWeight: 'lighter',
      fontSize: '14px',
    }),
    placeholder: (base: any) => ({
      ...base,
      color: '#80808060',
      fontWeight: 'lighter',
      fontSize: '14px',
    }),

  };

  const FilterSchema = Yup.object().shape({
    edad: Yup.number()
      .required('Este campo es requerido')
      .typeError('La edad debe ser un número')
      .min(0, 'La edad mínima es 0 años'),
    especialidad: Yup.string()
      .required('Este campo es requerido'),
    categoria: Yup.string()
      .required('Este campo es requerido'),
    sucursal: Yup.string()
      .required('Este campo es requerido'),
    doctor: doctorFilter ? Yup.string().required('Este campo es requerido') : Yup.string(),
  });

  const handleSubmitForm = (values: any) => {
    if (values.especialidad == "9" && values.sucursal == "3") {
      setFormValues(values);
      setShowWarningModal(true);
    } else {
      setDataHour({
        specialty: values.especialidad,
        categorie: values.categoria,
        facility: values.sucursal,
        doctor: values.doctor,
        preparacion: values.preparacion,
        dataLabels,
        uid
      });
    }
  };

  const handleContinue = () => {
    setShowWarningModal(false);
    if (formValues) {
      setDataHour({
        specialty: formValues.especialidad,
        categorie: formValues.categoria,
        facility: formValues.sucursal,
        doctor: formValues.doctor,
        preparacion: formValues.preparacion,
        dataLabels,
        uid
      });
    }
  };



  return (
    <div className="filterFormContainer">
      <Formik
        innerRef={formRef}
        initialValues={{
          especialidad: dataHour.specialty || '',
          edad: clientData.edad,
          categoria: dataHour.categorie,
          sucursal: dataHour.facility,
          doctor: '',
          preparacion: dataHour.preparacion,
        }}
        validationSchema={FilterSchema}
        onSubmit={handleSubmitForm}
        validateOnMount={false}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({
          values,
          errors,
          handleSubmit,
          setFieldValue
        }) => (
          <form onSubmit={handleSubmit} className="form">
            <label className="labelForm">Especialidad</label>
            <div
              data-event="change_especialidad"
              data-value={values.especialidad && specialties.find((specialtie) => specialtie.value === values.especialidad)?.label || ''}
            >
              <ModalPreparations
                prestacionName={values.categoria && categories.find((categorie) => categorie.value?.id === values.categoria)?.label || ''}
                specialtyId={Number(values.especialidad)}
              />
              <Select
                value={preSelectedSpecialty !== null && specialties[preSelectedSpecialty]
                  ? specialties[preSelectedSpecialty]
                  : specialties.find((specialtie) => specialtie.value === values.especialidad)}
                placeholder="Seleccione Especialidad"
                className="formSelector"
                options={specialties}
                styles={colourStyles}
                onChange={async (specialtie: any) => {
                  setShowFacility(false);
                  values.sucursal = '';
                  values.categoria = '';
                  await findCategory(specialtie.value);
                  values.especialidad = specialtie.value;
                  dataLabels.specialty = specialtie.label;


                }}
                isDisabled={preSelectedSpecialty !== null}
              />
            </div>
            <label className="labelForm">Prestación</label>
            <div
              data-event="change_prestacion"
              data-value={values.categoria && categories.find((categorie) => categorie.value?.id === values.categoria)?.label || ''}
            >
              <Select
                value={values.categoria && categories.find((categorie) => categorie.value?.id === values.categoria)}
                placeholder={loadingCategories ? 'Cargando prestaciones...' : 'Selecciona una prestacion'}
                className="formSelector"
                styles={colourStyles}
                options={categories}
                onChange={(categorie: any) => {
                  values.sucursal = '';
                  setSucursales([]);
                  if (values.edad) {
                    findFacilitiesByCategorie(categorie.value.id, values.edad);
                  } else {
                    findFacilitiesByCategorie(categorie.value.id, '100');
                  }
                  values.categoria = categorie.value.id;
                  dataLabels.category = categorie.label;
                  values.preparacion = categorie.value.preparacion;


                }}
                isDisabled={!showCategories}
              />
            </div>
            {(values.categoria && categories.find((cat) => cat.value?.id === values.categoria)) && (
              <>
                <label className="labelFormValues">Valores</label>
                {Number(categories.find((cat) => cat.value?.id === values.categoria)?.value?.valor_fn1) > 0 ? (() => {
                  const selectedCategory = categories.find((cat) => cat.value?.id === values.categoria);
                  const categoryValue = selectedCategory?.value;
                  const isExterno = categoryValue?.pc_externo === 1;
                  return (
                    <div className="valuesContainer">
                      <div className="valueColumn">
                        <span className="valueTitle fonasaText">
                          {isExterno ? 'Bono externo' : 'Fonasa Nivel 1'}
                        </span>
                        <span className="valueAmount fonasaText">
                          ${Number(categoryValue?.valor_fn1).toLocaleString('es-CL')}
                        </span>
                      </div>
                    <div className="valueDivider"></div>
                    <div className="valueColumnRight">
                      <span className="valueTitle">Particular</span>
                      <span className="valueAmount">
                        ${Number(categoryValue?.valor_particular).toLocaleString('es-CL')}
                      </span>
                    </div>
                  </div>
                  );
                })() : (
                  <>
                    <div className="valuesContainer particularOnly">
                      <div>
                        <div className="valueTitle">Particular:</div>
                        <div className="valueAmount">
                          ${Number(categories.find((cat) => cat.value?.id === values.categoria)?.value?.valor_particular).toLocaleString('es-CL')}
                        </div>
                      </div>
                    </div>
                    <div className="particularNote">*Prestación disponible solo valor particular.</div>
                  </>
                )}
              </>
            )}
            <label className="labelForm">Sucursal</label>
            <div
              data-event="change_sucursal"
              data-value={values.sucursal && sucursales.find((sucursal) => sucursal.value === values.sucursal)?.label || ''}
            >
              <Select
                value={values.sucursal && sucursales.find((sucursal) => sucursal.value === values.sucursal)}
                placeholder={loadingFacilities ? 'Cargando sucursales...' : 'Selecciona una sucursal'}
                className="formSelector"
                options={sucursales}
                styles={colourStyles}
                isDisabled={!showFacility}
                onChange={(sucursal: any) => {
                  findDoctors(sucursal.value, values.categoria);
                  values.sucursal = sucursal.value;
                  dataLabels.facility.name = sucursal.label;
                  dataLabels.facility.street = sucursal.street;
                  dataLabels.facility.state = sucursal.state;


                }}
              />
            </div>
            {doctorFilter
              ? (
                <>
                  <label className="labelForm">Profesional</label>
                  <div
                    data-event="change_profesional"
                    data-value={values.doctor && doctors.find((doctor) => doctor.value === values.doctor)?.label || ''}
                  >
                    <Select
                      placeholder={loadingDoctors ? 'Cargando doctores...' : 'Selecciona un doctor'}
                      className="formSelector"
                      options={doctors}
                      isDisabled={!showDoctors}
                      onChange={(doctor: any) => {
                        values.doctor = doctor.value;


                      }}
                      styles={colourStyles}
                    />
                  </div>
                </>
              ) : null}
            <label className="labelForm">Edad</label>
            <Field
              id="edad"
              name="edad"
              type="number"
              placeholder={values.edad ? values.edad.toString() : 'Seleccione Edad'}
              className="formSelector"
              onChange={(e: any) => {
                const edad = e.target.value;
                setFieldValue('edad', edad);
                clientData.edad = edad;


              }}
              data-event="input_edad"
              data-value={values.edad || ''}
            />
            {errors.edad === 'La edad debe ser un número' ? (
              <div className="error">{errors.edad}</div>
            ) : null}
            {isCharge
              ? (<Loader color="#f9f9f9" />)
              : (
                <div className="buttonsContainer">
                  <a
                    href="https://maiposalud.cl/reserva-de-hora/"
                    className="buttonSubmit btnVolver"

                    data-event="click_btn_volver_a_inicio"
                    data-value="desde_busqueda_hora"
                  >
                    Volver
                  </a>
                  <button
                    type="submit"
                    className="buttonSubmit btnBuscar"

                    data-event="click_btn_buscar_hora"
                    data-value=""
                  >
                    Buscar
                  </button>
                </div>
              )}
            {(errors.edad || errors.categoria
              || errors.especialidad || errors.sucursal
              || errors.doctor) && errors.edad !== 'La edad debe ser un número'
              ? (<div className="error">Debes rellenar todos los campos</div>)
              : null}
          </form>
        )}
      </Formik>

      <WarningPopup
        isOpen={showWarningModal}
        onClose={() => setShowWarningModal(false)}
        onContinue={handleContinue}
        title="Importante"
        message="En sucursal de La Cisterna, nuestra área de Kinesiología se encuentra en 2do piso, por el momento no poseemos ascensor habilitado."
        subMessage="Le solicitamos considerar esta condición al momento de tomar su tratamiento."
        continueText="Entiendo, continuar"
        cancelText="Buscar otra hora"
      />
    </div>
  );
};

export default Filter;
